/**
 * «Non rifiutabile» — la regola, la conseguenza e le note del server tradotte.
 *
 * ## La cosa che non è scritta da nessuna parte
 *
 * Spegnere «Rifiutabile» sembra dire «il responsabile può solo prendere atto». Non è quello
 * che fa. Il server (`LeaveWorkflowController`) calcola
 *
 * ```csharp
 * bool effectiveIsRejectable = lt.IsRejectable && (reason == null || reason.IsRejectable);
 * bool nonRejectableAuto = !effectiveIsRejectable
 *     && await _config.GetBoolAsync("leave.nonRejectableAutoApprove", true);
 * ```
 *
 * e se `nonRejectableAuto` è vero **approva la richiesta al momento dell'invio**. Quindi il
 * responsabile non «prende atto»: non vede nemmeno la richiesta in coda. È una differenza che
 * cambia il senso del flag, e va detta dove il flag si accende.
 *
 * ## Basta uno dei due
 *
 * La regola è una **AND**: tipo e causale devono essere entrambi rifiutabili perché la
 * richiesta lo sia. Non è «la causale vince sul tipo» — è «vince il più restrittivo». Con una
 * causale non rifiutabile dentro un tipo rifiutabile, la richiesta non è rifiutabile.
 *
 * ## Il 403, e quando è raggiungibile
 *
 * `POST …/reject` risponde `403 LEAVE_REASON_NOT_REJECTABLE` con `meta.blockingLevel` a
 * `leaveType` o `leaveReason`. Con l'auto-approvazione dei non rifiutabili attiva (default)
 * quel 403 si vede raramente, perché la richiesta è già approvata prima di arrivare in coda:
 * lo si incontra quando quel comportamento è spento sul tenant. Il messaggio lo dice senza
 * affermare quale dei due casi sia — non lo sappiamo da qui.
 */

/** Il livello che blocca il rifiuto, come lo dichiara il `meta` del server. */
export type BlockingLevel = 'leaveType' | 'leaveReason';

/**
 * La richiesta è rifiutabile?
 *
 * `undefined` su uno dei due significa «non lo so»: si assume rifiutabile, che è il default
 * del BE (`IsRejectable = true`). Assumere il contrario mostrerebbe avvisi su tipi normali.
 */
export function rejectable(tipo?: boolean | null, reason?: boolean | null): boolean {
  return tipo !== false && reason !== false;
}

/**
 * Cosa comporta spegnere «Rifiutabile», detto per chi sta configurando.
 *
 * @param autoApproveActive il tenant-attribute `leave.nonRejectableAutoApprove` (default true)
 */
export function nonRejectableConsequence(autoApproveActive: boolean): string {
  return autoApproveActive
    ? 'Con questa configurazione la richiesta viene approvata al momento dell’invio: non arriva in coda a nessuno, e il workflow di approvazione non viene applicato.'
    : 'Il responsabile non potrà respingerla: il rifiuto viene rifiutato dal sistema. La richiesta resta in coda in attesa di essere approvata.';
}

/** L'avviso completo per il modulo della motivazione. `null` se la motivazione è rifiutabile. */
export function nonRejectableReasonNotice(
  isRejectable: boolean | undefined,
  autoApproveAttivo: boolean,
): string | null {
  if (isRejectable !== false) return null;
  return `Chi sceglie questa motivazione **non può vedersi rifiutare** la richiesta, anche se il tipo di assenza lo permette. ${nonRejectableConsequence(autoApproveAttivo)}`;
}

/**
 * Il messaggio per il `403` sul rifiuto: dice **quale** dei due livelli blocca e dove si
 * cambia, invece del solo «non è rifiutabile» che lascia l'approvatore senza mossa.
 */
export function lockedRejectionMessage(level?: BlockingLevel | string | null): string {
  const dove = level === 'leaveReason'
    ? 'la causale scelta dal richiedente'
    : level === 'leaveType'
      ? 'il tipo di assenza'
      : 'la configurazione';
  return `Questa richiesta non si può respingere: ${dove} è marcata come non rifiutabile. `
    + 'Si può solo approvarla o lasciarla in attesa. Se è un errore, il flag «Rifiutabile» si cambia in Amministrazione → Config assenze.';
}

/**
 * Le note che il server scrive sugli eventi, in italiano.
 *
 * Sono stringhe tecniche in inglese (`AddEvent(lr, "auto_approved", "system", null,
 * "Auto-approved: non-rejectable")`) e finiscono **così come sono** nello storico della
 * richiesta, sotto gli occhi di chi ha chiesto le ferie. Tradurle è l'ultimo passo di quella
 * catena: senza, la riga più importante dello storico — perché nessuno ha approvato — si
 * legge come un messaggio di debug.
 *
 * Una nota che non riconosciamo torna **invariata**: il vocabolario del BE può crescere, e
 * nascondere una nota nuova sarebbe peggio che mostrarla in inglese.
 */
export function translatedEventNote(note?: string | null): string | null {
  /* Vuoto e assente sono la stessa cosa per chi legge: `null`, così il chiamante ha una sola
     condizione da scrivere invece di due. */
  const n = (note ?? '').trim();
  if (!n) return null;

  if (/^Auto-approved:\s*non-rejectable$/i.test(n)) {
    return 'Approvata automaticamente: la richiesta non è rifiutabile (per il tipo o per la causale), e l’azienda approva subito ciò che non si può respingere.';
  }
  if (/^Auto-approved:\s*approvalWorkflow\s*'?auto'?$/i.test(n)) {
    return 'Approvata automaticamente: il tipo di assenza è configurato senza passaggi di approvazione.';
  }
  const policy = /^Auto-approved:\s*policy\s*'([^']+)'$/i.exec(n);
  if (policy) {
    const label: Record<string, string> = {
      always: 'l’auto-approvazione del tipo è impostata su «sempre»',
      'with-document': 'il documento richiesto era presente all’invio',
      'with-substitute': 'il sostituto era indicato all’invio',
    };
    const why = label[policy[1]] ?? `politica «${policy[1]}»`;
    return `Approvata automaticamente: ${why}.`;
  }
  /* `n` e non `nota`: è la stessa stringa già ripulita dagli spazi, e il tipo non porta più
     l'`undefined` che il parametro ammette. */
  return n;
}
